import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, Upload, IdCard, Building2, User, Phone, MapPin, ScanFace,
  CheckCircle2, AlertCircle, RefreshCw, Eye, LogOut,
} from "lucide-react";
import api from "@/lib/api";
import { loadFaceApi, getCamera, stopStream, detectFace, snapshotVideo, detectBlink } from "@/lib/faceApi";

/**
 * Iter v34 — Step 3 of Karyawan Onboarding.
 * After email verification + login, karyawan completes profile:
 *   1. Personal data (NIK, phone, address, birth_date, department category)
 *   2. KTP photo (camera or upload)
 *   3. Bank account
 *   4. Face setup (liveness + 128-dim descriptor via face-api)
 * Submit → PUT /api/employees/me/profile → status `pending_verification`
 * (waiting for HR approval before clock-in is enabled).
 */

async function compressImage(file, maxWidth = 1280, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(1, maxWidth / img.width);
        const w = img.width * ratio, h = img.height * ratio;
        const cv = document.createElement("canvas");
        cv.width = w; cv.height = h;
        cv.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(cv.toDataURL("image/jpeg", quality).split(",")[1]);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Iter v34bw (#1) — Compute Laplacian variance of an image (blur/clarity metric).
// Higher variance = sharper. Returns 0 if unable to compute.
// Sampled to max 400px width for perf.
function computeLaplacianVariance(imageB64) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        try {
          const targetW = Math.min(400, img.width);
          const scale = targetW / img.width;
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const cv = document.createElement("canvas");
          cv.width = w; cv.height = h;
          const ctx = cv.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h).data;
          // Grayscale
          const gray = new Float32Array(w * h);
          for (let i = 0, j = 0; i < data.length; i += 4, j++) {
            gray[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          }
          // 3x3 Laplacian: center=4, 4-neighbors=-1
          let sum = 0, sumSq = 0, count = 0;
          for (let y = 1; y < h - 1; y++) {
            for (let x = 1; x < w - 1; x++) {
              const idx = y * w + x;
              const lap = 4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - w] - gray[idx + w];
              sum += lap;
              sumSq += lap * lap;
              count++;
            }
          }
          const mean = sum / count;
          const variance = sumSq / count - mean * mean;
          resolve(Math.max(0, variance));
        } catch { resolve(0); }
      };
      img.onerror = () => resolve(0);
      img.src = `data:image/jpeg;base64,${imageB64}`;
    } catch { resolve(0); }
  });
}

const inputCls = "w-full border border-stone-300 bg-white px-3 py-2 text-sm focus:border-terra-500 focus:ring-1 focus:ring-terra-500";
const lbl = "text-[11px] uppercase tracking-widest text-stone-600 font-medium mb-1 flex items-center gap-1.5";

export default function KaryawanLengkapiProfil() {
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [me, setMe] = useState(null);
  const [appSettings, setAppSettings] = useState({ ktp_clarity_threshold: 100.0, blocked_bank_keywords: ["dana"] });
  const [form, setForm] = useState({
    nik: "", phone: "", address: "", birth_date: "",
    department_category: "",
    ktp_photo_b64: "",
    bank_name: "", bank_account_holder: "", bank_account_number: "",
    face_descriptor: null, face_photo_b64: "",
    // Iter v34bw (#2) — Multi-angle face registration (5 pose).
    // Structure: [{ pose: 'front'|'left'|'right'|'up'|'down', descriptor: number[], photo_b64: string }]
    face_descriptors_multi: [],
  });
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1); // 1=Data,2=KTP,3=Bank,4=Wajah
  const totalSteps = 4;

  // KTP
  const [ktpMode, setKtpMode] = useState("upload");
  const [ktpStream, setKtpStream] = useState(null);
  const [ktpClarity, setKtpClarity] = useState(null); // null | number
  const [ktpChecking, setKtpChecking] = useState(false);
  const ktpVideoRef = useRef(null);
  const ktpCanvasRef = useRef(null);

  // Face
  const [faReady, setFaReady] = useState(false);
  const [faError, setFaError] = useState(null);
  const [faStatus, setFaStatus] = useState("");
  const [faceStream, setFaceStream] = useState(null);
  const [livenessOk, setLivenessOk] = useState(false);
  const [livenessTick, setLivenessTick] = useState(null);
  const faceVideoRef = useRef(null);
  const faceCanvasRef = useRef(null);

  // Iter v34bw (#2) — Multi-angle face wizard (5 pose seperti Face ID iPhone).
  // Pose 'front' wajib pertama karena dipakai untuk liveness + primary descriptor.
  const POSES = [
    { key: "front", label: "Menghadap Depan", tip: "Tatap lurus ke kamera, posisikan wajah di tengah oval" },
    { key: "left",  label: "Miring ke Kiri", tip: "Putar kepala sekitar 30° ke KIRI Anda (kamera lihat pipi kanan Anda)" },
    { key: "right", label: "Miring ke Kanan", tip: "Putar kepala sekitar 30° ke KANAN Anda (kamera lihat pipi kiri Anda)" },
    { key: "up",    label: "Angkat Dagu",    tip: "Miringkan kepala sedikit ke atas (angkat dagu ~15°)" },
    { key: "down",  label: "Turunkan Dagu",  tip: "Miringkan kepala sedikit ke bawah (turunkan dagu ~15°)" },
  ];
  const [poseIdx, setPoseIdx] = useState(0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/department-categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
    // Iter v34bw (#1) — Fetch KTP threshold + blocked bank keywords.
    api.get("/settings").then((r) => setAppSettings({
      ktp_clarity_threshold: r.data?.ktp_clarity_threshold ?? 100.0,
      blocked_bank_keywords: r.data?.blocked_bank_keywords ?? ["dana"],
    })).catch(() => {});
    api.get("/employees/me/profile").then((r) => {
      setMe(r.data);
      if (r.data?.profile_completed) {
        // sudah selesai → cek status
        if (r.data?.status === "active") {
          toast.info("Profil sudah lengkap & sudah aktif. Mengarahkan ke absensi.");
          setTimeout(() => nav("/karyawan/absen"), 600);
        } else {
          toast.info("Profil sudah dikirim. Menunggu persetujuan HR.");
        }
      }
      // Prefill from existing employee stub
      setForm((f) => ({
        ...f,
        nik: r.data?.nik || "",
        phone: r.data?.phone || "",
        address: r.data?.address || "",
        birth_date: r.data?.birth_date || "",
        department_category: r.data?.department_category || "",
        bank_name: r.data?.bank_name || "",
        bank_account_holder: r.data?.bank_account_holder || "",
        bank_account_number: r.data?.bank_account_number || "",
      }));
    }).catch(() => { /* nothing */ });
  }, [nav]);

  useEffect(() => () => { stopStream(ktpStream); stopStream(faceStream); }, [ktpStream, faceStream]);

  // ----- KTP Capture -----
  const startKtpCam = async () => {
    setKtpMode("camera");
    try {
      // Iter v34g (#bugfix) — wait 2 animation frames so React has time to MOUNT
      // the <video> element in DOM before we attach the stream. iOS Safari
      // is especially strict: srcObject + play() must hit a real attached node.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (!ktpVideoRef.current) {
        await new Promise((r) => setTimeout(r, 150));
      }
      if (!ktpVideoRef.current) throw new Error("Video element belum siap, coba lagi");
      const s = await getCamera(ktpVideoRef.current, { facingMode: "environment", width: 1280, height: 720 });
      setKtpStream(s);
    } catch (e) { toast.error(e.message || "Gagal akses kamera"); setKtpMode("upload"); }
  };
  const stopKtpCam = () => { stopStream(ktpStream); setKtpStream(null); setKtpMode("upload"); };

  // Iter v34bw (#1) — Setelah dapat foto KTP (kamera atau upload), hitung Laplacian variance.
  // Jika < threshold → tolak & minta ulang.
  const validateKtpClarity = async (b64) => {
    setKtpChecking(true);
    try {
      const variance = await computeLaplacianVariance(b64);
      setKtpClarity(variance);
      const th = Number(appSettings.ktp_clarity_threshold || 100);
      if (variance < th) {
        toast.error(`Foto KTP kurang jelas (skor ${variance.toFixed(1)} < ${th}). Ambil ulang dengan pencahayaan lebih baik.`);
        return false;
      }
      toast.success(`Foto KTP jelas (skor ${variance.toFixed(1)} ≥ ${th}).`);
      return true;
    } finally { setKtpChecking(false); }
  };

  const captureKtp = async () => {
    const b64 = snapshotVideo(ktpVideoRef.current, ktpCanvasRef.current, 0.85);
    if (!b64) return toast.error("Kamera belum aktif");
    const ok = await validateKtpClarity(b64);
    if (!ok) return; // reject; user can retake
    set("ktp_photo_b64", b64);
    stopKtpCam();
    toast.success("Foto KTP berhasil diambil");
  };
  const onKtpUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar");
    try {
      const b64 = await compressImage(file);
      const ok = await validateKtpClarity(b64);
      if (!ok) return;
      set("ktp_photo_b64", b64);
      toast.success("Foto KTP berhasil diunggah");
    } catch { toast.error("Gagal memproses gambar"); }
    finally { e.target.value = ""; }
  };

  // ----- Face Setup -----
  const initFaceApi = async () => {
    setFaError(null); setFaReady(false);
    try {
      await loadFaceApi((msg) => setFaStatus(msg));
      setFaReady(true);
      setFaStatus("Model siap. Klik 'Mulai Kamera Wajah'.");
    } catch (e) {
      setFaError(e.message || "Gagal memuat model wajah");
    }
  };
  useEffect(() => { initFaceApi(); }, []);

  // Iter v34g (#bugfix) — separate "starting" flag so we can mount the <video>
  // element BEFORE we attach the MediaStream (React must commit the DOM first).
  const [faceStarting, setFaceStarting] = useState(false);

  const startFaceCam = async () => {
    setFaceStarting(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (!faceVideoRef.current) {
        await new Promise((r) => setTimeout(r, 150));
      }
      if (!faceVideoRef.current) throw new Error("Video element belum siap, coba lagi");
      const s = await getCamera(faceVideoRef.current, { facingMode: "user", width: 640, height: 480 });
      setFaceStream(s);
      setFaStatus("Kamera aktif. Tekan 'Cek Liveness (Kedipkan Mata)'.");
    } catch (e) { toast.error(e.message || "Gagal akses kamera"); }
    finally { setFaceStarting(false); }
  };
  const stopFaceCam = () => { stopStream(faceStream); setFaceStream(null); setLivenessOk(false); setLivenessTick(null); setFaceStarting(false); };

  const runLiveness = async () => {
    setLivenessOk(false); setLivenessTick(null);
    setFaStatus("Tatap kamera, lalu kedipkan mata 1–2 kali dalam 8 detik. Pastikan poni tidak menutupi mata.");
    try {
      const res = await detectBlink(faceVideoRef.current, {
        timeoutMs: 8000,
        onTick: (t) => setLivenessTick(t),
      });
      if (res?.blinkDetected || res?.ok) {
        setLivenessOk(true);
        setFaStatus("Liveness OK ✓. Sekarang klik 'Ambil Wajah'.");
        toast.success("Liveness berhasil — silakan ambil wajah");
      } else {
        const tipMsg = (res?.minEar != null && res?.maxEar != null && res.maxEar - res.minEar < 0.02)
          ? `Tidak terdeteksi perubahan mata (EAR ${res.minEar.toFixed(2)}-${res.maxEar.toFixed(2)}). Sibak rambut dari mata, lalu kedip lebih jelas.`
          : "Liveness gagal. Coba lagi dengan pencahayaan lebih terang & kedipkan mata jelas.";
        setFaStatus(tipMsg);
        toast.error(tipMsg);
      }
    } catch (e) {
      setFaStatus(e?.message || "Liveness error");
      toast.error("Liveness error: " + (e?.message || ""));
    }
  };

  const captureFace = async () => {
    const currentPose = POSES[poseIdx];
    // Liveness hanya wajib untuk pose 'front' (anti-photo protection).
    if (currentPose.key === "front" && !livenessOk) return toast.error("Selesaikan Liveness Check dulu (kedipkan mata)");
    try {
      const det = await detectFace(faceVideoRef.current);
      if (!det) return toast.error("Wajah tidak terdeteksi. Sesuaikan pose & pencahayaan.");
      const photoB64 = snapshotVideo(faceVideoRef.current, faceCanvasRef.current, 0.75);
      const entry = { pose: currentPose.key, descriptor: Array.from(det.descriptor), photo_b64: photoB64 || "" };
      setForm((f) => {
        const arr = (f.face_descriptors_multi || []).filter((x) => x.pose !== currentPose.key);
        arr.push(entry);
        // Primary descriptor = pose 'front' (dipakai backend legacy matching + duplicate check).
        const primary = arr.find((x) => x.pose === "front");
        return {
          ...f,
          face_descriptors_multi: arr,
          face_descriptor: primary ? primary.descriptor : f.face_descriptor,
          face_photo_b64: primary ? primary.photo_b64 : f.face_photo_b64,
        };
      });
      toast.success(`Pose "${currentPose.label}" tersimpan (${poseIdx + 1}/${POSES.length})`);
      if (poseIdx < POSES.length - 1) {
        setPoseIdx(poseIdx + 1);
        // Reset liveness check untuk pose berikut jika perlu (tidak wajib).
      } else {
        // Semua pose selesai.
        stopFaceCam();
        toast.success("Semua 5 pose berhasil direkam!");
      }
    } catch (e) { toast.error(e?.message || "Gagal ambil wajah"); }
  };

  const resetFaceWizard = () => {
    setForm((f) => ({ ...f, face_descriptor: null, face_photo_b64: "", face_descriptors_multi: [] }));
    setPoseIdx(0);
    setLivenessOk(false);
    setLivenessTick(null);
  };

  // ----- Submit -----
  const submit = async () => {
    if (!form.nik.trim() || form.nik.length !== 16) return toast.error("NIK harus 16 digit");
    if (!form.phone.trim()) return toast.error("No HP wajib");
    if (!form.address.trim()) return toast.error("Alamat wajib");
    if (!form.department_category) return toast.error("Kategori departemen wajib");
    if (!form.ktp_photo_b64) return toast.error("Foto KTP wajib");
    if (!form.bank_name.trim() || !form.bank_account_holder.trim() || !form.bank_account_number.trim()) return toast.error("Rekening bank wajib lengkap");
    // Iter v34bw (#1) — Reject blocked bank keywords (mis. "dana").
    const bnLow = form.bank_name.trim().toLowerCase();
    const hit = (appSettings.blocked_bank_keywords || []).find((k) => k && bnLow.includes(String(k).toLowerCase()));
    if (hit) return toast.error(`Nama bank "${form.bank_name.trim()}" mengandung kata terlarang "${hit}". E-wallet (mis. DANA) tidak diterima. Gunakan rekening BANK resmi.`);
    if (!form.face_descriptor || form.face_descriptor.length !== 128) return toast.error("Setup wajah wajib (Step 4) — minimal pose Depan");
    // Iter v34bw (#2) — Wajib 5 pose multi-angle.
    if ((form.face_descriptors_multi || []).length < POSES.length) {
      return toast.error(`Lengkapi semua 5 pose wajah. Baru ${(form.face_descriptors_multi || []).length}/${POSES.length} tersimpan.`);
    }
    setBusy(true);
    try {
      await api.put("/employees/me/profile", form);
      toast.success("Data dikirim. Menunggu persetujuan HR.");
      setTimeout(() => nav("/karyawan/absen"), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal kirim data");
    } finally { setBusy(false); }
  };

  const onLogout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen bg-stone-50 py-6">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-terra-700 font-medium">Onboarding Karyawan · Step 3</p>
            <h1 className="font-display text-3xl font-bold text-stone-900 mt-1">Lengkapi Data Diri Anda</h1>
            <p className="text-sm text-stone-600 mt-1">Email Anda <b>{me?.email || "..."}</b> telah diverifikasi. Lengkapi 4 langkah berikut, lalu tunggu persetujuan HR.</p>
          </div>
          <button onClick={onLogout} className="text-xs text-stone-500 hover:text-red-600 flex items-center gap-1" data-testid="profil-logout"><LogOut className="h-3 w-3" /> Keluar</button>
        </div>

        {/* Progress bar */}
        <div className="bg-white border border-stone-200 p-3 mb-4 flex items-center gap-2" data-testid="profil-progress">
          {[1,2,3,4].map((n) => (
            <React.Fragment key={n}>
              <div className={`flex-1 text-center text-[11px] font-medium py-2 border ${step === n ? "bg-terra-500 text-white border-terra-500" : step > n ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-50 text-stone-500 border-stone-200"}`}>
                {n}. {["Data","KTP","Bank","Wajah"][n-1]}
                {step > n && <CheckCircle2 className="h-3 w-3 inline ml-1" />}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white border border-stone-200 p-6 space-y-6" data-testid="profil-form">
          {/* STEP 1: Data Pribadi */}
          {step === 1 && (
            <section className="space-y-4" data-testid="profil-step-data">
              <h3 className="font-display text-base font-semibold text-stone-900 flex items-center gap-2"><User className="h-4 w-4 text-terra-600" /> Data Pribadi</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className={lbl}><IdCard className="h-3 w-3" /> NIK (KTP) *</label><input className={inputCls} value={form.nik} onChange={(e) => set("nik", e.target.value.replace(/\D/g, ""))} maxLength={16} required data-testid="profil-nik" /></div>
                <div><label className={lbl}><Phone className="h-3 w-3" /> No HP *</label><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} required data-testid="profil-phone" /></div>
                <div><label className={lbl}>Tanggal Lahir</label><input type="date" className={inputCls} value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} data-testid="profil-birth" /></div>
                <div>
                  <label className={lbl}><Building2 className="h-3 w-3" /> Kategori Departemen *</label>
                  <select className={inputCls} value={form.department_category} onChange={(e) => set("department_category", e.target.value)} required data-testid="profil-category">
                    <option value="">— Pilih Kategori —</option>
                    {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}><MapPin className="h-3 w-3" /> Alamat *</label>
                  <textarea rows={2} className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} required data-testid="profil-address" />
                </div>
              </div>
            </section>
          )}

          {/* STEP 2: KTP */}
          {step === 2 && (
            <section className="space-y-4" data-testid="profil-step-ktp">
              <h3 className="font-display text-base font-semibold text-stone-900 flex items-center gap-2"><IdCard className="h-4 w-4 text-terra-600" /> Foto KTP</h3>
              <p className="text-xs text-stone-500">Pastikan foto KTP jelas, tidak buram. Sistem akan mengompres otomatis.</p>
              {form.ktp_photo_b64 ? (
                <div className="flex items-start gap-4">
                  <img src={`data:image/jpeg;base64,${form.ktp_photo_b64}`} alt="KTP" className="max-h-48 border border-stone-200" data-testid="profil-ktp-preview" />
                  <button type="button" onClick={() => set("ktp_photo_b64", "")} className="text-xs text-red-600 hover:underline" data-testid="profil-ktp-clear">Hapus &amp; ganti</button>
                </div>
              ) : ktpMode === "camera" ? (
                <div className="space-y-2">
                  <div className="relative bg-stone-900 aspect-video max-w-md overflow-hidden">
                    <video ref={ktpVideoRef} className="w-full h-full object-cover" muted autoPlay playsInline />
                    <canvas ref={ktpCanvasRef} className="hidden" />
                    {/* Iter v34bw (#1) — KTP frame guide (rasio KTP ≈ 1,586:1 sama seperti kartu kredit ISO/IEC 7810 ID-1). */}
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                      <div
                        className="border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
                        style={{ width: "82%", aspectRatio: "1.586 / 1", borderRadius: "10px" }}
                        data-testid="ktp-guide-frame"
                      >
                        {/* Corner ticks */}
                        <div className="absolute -top-1 -left-1 w-4 h-4 border-t-4 border-l-4 border-terra-400" />
                        <div className="absolute -top-1 -right-1 w-4 h-4 border-t-4 border-r-4 border-terra-400" />
                        <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-4 border-l-4 border-terra-400" />
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-4 border-r-4 border-terra-400" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] text-white/90 bg-black/40 px-2 py-0.5 rounded">
                      Posisikan KTP di dalam bingkai. Ambang kejelasan: {appSettings.ktp_clarity_threshold}
                    </div>
                    {!ktpStream && (
                      <div className="absolute inset-0 flex items-center justify-center text-white/70 text-xs">Memulai kamera…</div>
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    <button type="button" onClick={captureKtp} disabled={!ktpStream || ktpChecking} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="profil-ktp-capture">
                      <Camera className="h-3.5 w-3.5" /> {ktpChecking ? "Mengecek…" : "Ambil Foto"}
                    </button>
                    <button type="button" onClick={stopKtpCam} className="inline-flex items-center px-3 py-2 text-xs border border-stone-300 hover:bg-stone-50">Batal</button>
                    {ktpClarity !== null && (
                      <span className={`text-[11px] font-mono ${ktpClarity < Number(appSettings.ktp_clarity_threshold||100) ? "text-red-700" : "text-emerald-700"}`} data-testid="ktp-clarity-score">
                        Skor terakhir: {ktpClarity.toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={startKtpCam} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-stone-300 hover:bg-stone-50" data-testid="profil-ktp-camera"><Camera className="h-3.5 w-3.5" /> Ambil dari Kamera</button>
                  <label className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-stone-300 hover:bg-stone-50 cursor-pointer" data-testid="profil-ktp-upload-label">
                    <Upload className="h-3.5 w-3.5" /> Upload File KTP
                    <input type="file" accept="image/*" onChange={onKtpUpload} className="hidden" data-testid="profil-ktp-upload" />
                  </label>
                </div>
              )}
            </section>
          )}

          {/* STEP 3: Bank */}
          {step === 3 && (
            <section className="space-y-4" data-testid="profil-step-bank">
              <h3 className="font-display text-base font-semibold text-stone-900 flex items-center gap-2"><Building2 className="h-4 w-4 text-terra-600" /> Rekening Bank</h3>
              <p className="text-xs text-stone-500">Digunakan untuk transfer gaji & reimbursement.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={lbl}>Nama Bank *</label>
                  <input className={inputCls} value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="contoh: BCA, Mandiri" required data-testid="profil-bank-name" />
                  {/* Iter v34bw (#1) — Live warning saat nama bank mengandung kata terlarang. */}
                  {(() => {
                    const bnLow = (form.bank_name || "").toLowerCase();
                    const hit = (appSettings.blocked_bank_keywords || []).find((k) => k && bnLow.includes(String(k).toLowerCase()));
                    if (hit) return (
                      <p className="mt-1 text-[11px] text-red-700 flex items-start gap-1" data-testid="profil-bank-error">
                        <span>⚠</span>
                        <span>Nama bank mengandung kata terlarang <b>&quot;{hit}&quot;</b>. E-wallet (mis. DANA) tidak diterima. Gunakan rekening BANK resmi.</span>
                      </p>
                    );
                    return null;
                  })()}
                </div>
                <div><label className={lbl}>Nama Pemilik *</label><input className={inputCls} value={form.bank_account_holder} onChange={(e) => set("bank_account_holder", e.target.value)} required data-testid="profil-bank-holder" /></div>
                <div><label className={lbl}>Nomor Rekening *</label><input className={inputCls} value={form.bank_account_number} onChange={(e) => set("bank_account_number", e.target.value.replace(/\D/g, ""))} required data-testid="profil-bank-number" /></div>
              </div>
            </section>
          )}

          {/* STEP 4: Face — Iter v34bw (#2) Multi-angle 5-pose wizard */}
          {step === 4 && (
            <section className="space-y-4" data-testid="profil-step-face">
              <h3 className="font-display text-base font-semibold text-stone-900 flex items-center gap-2"><ScanFace className="h-4 w-4 text-terra-600" /> Setup Pengenalan Wajah — 5 Pose</h3>
              <p className="text-xs text-stone-500">Direkam <b>5 pose</b> (depan, kiri, kanan, atas, bawah) seperti daftar Face ID di iPhone — meningkatkan akurasi absensi dari berbagai sudut.</p>

              {faError && (
                <div className="bg-red-50 border border-red-200 p-3 text-xs text-red-700 flex items-center gap-2">
                  <AlertCircle className="h-3.5 w-3.5" /> {faError}
                  <button onClick={initFaceApi} className="ml-auto text-xs underline" data-testid="profil-face-retry"><RefreshCw className="h-3 w-3 inline" /> Coba lagi</button>
                </div>
              )}
              {!faError && !faReady && (
                <div className="bg-stone-50 border border-stone-200 p-3 text-xs text-stone-700">{faStatus || "Memuat model wajah..."}</div>
              )}

              {/* Progress: 5 dots showing captured poses */}
              {faReady && (
                <div className="flex items-center justify-center gap-3 py-2" data-testid="profil-face-progress">
                  {POSES.map((p, i) => {
                    const captured = (form.face_descriptors_multi || []).some((x) => x.pose === p.key);
                    const isCurrent = i === poseIdx && (form.face_descriptors_multi || []).length < POSES.length;
                    return (
                      <div key={p.key} className="flex flex-col items-center gap-1">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-all ${
                          captured ? "bg-emerald-500 border-emerald-500 text-white" :
                          isCurrent ? "bg-white border-terra-500 text-terra-600 scale-110 shadow-lg" :
                          "bg-stone-100 border-stone-300 text-stone-400"
                        }`}>
                          {captured ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                        </div>
                        <div className={`text-[9px] uppercase tracking-wider text-center ${isCurrent ? "text-terra-700 font-semibold" : "text-stone-500"}`}>{p.key}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {(form.face_descriptors_multi || []).length === POSES.length ? (
                <div className="bg-emerald-50 border border-emerald-200 p-4" data-testid="profil-face-ok">
                  <div className="text-sm font-medium text-emerald-800 flex items-center gap-1.5 mb-3"><CheckCircle2 className="h-4 w-4" /> Semua 5 Pose Berhasil Direkam</div>
                  <div className="grid grid-cols-5 gap-2">
                    {form.face_descriptors_multi.map((x) => (
                      <div key={x.pose} className="text-center">
                        {x.photo_b64 && <img src={`data:image/jpeg;base64,${x.photo_b64}`} alt={x.pose} className="w-full aspect-square object-cover border border-emerald-300 mb-1" />}
                        <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold">{x.pose}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={resetFaceWizard} className="mt-3 text-xs text-emerald-700 underline" data-testid="profil-face-reset">Ulangi Semua Pose</button>
                </div>
              ) : faReady && (
                <div className="space-y-3">
                  <div className="bg-terra-50 border-l-4 border-terra-500 p-3">
                    <div className="text-sm font-semibold text-terra-800">Pose {poseIdx + 1}/{POSES.length}: {POSES[poseIdx].label}</div>
                    <div className="text-xs text-terra-700 mt-1">{POSES[poseIdx].tip}</div>
                  </div>

                  {!faceStream && !faceStarting ? (
                    <button onClick={startFaceCam} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-terra-500 text-white hover:bg-terra-600" data-testid="profil-face-start">
                      <Camera className="h-3.5 w-3.5" /> Mulai Kamera Wajah
                    </button>
                  ) : (
                    <>
                      <div className="relative bg-stone-900 max-w-md mx-auto aspect-[4/3] overflow-hidden">
                        <video ref={faceVideoRef} className="w-full h-full object-cover" muted autoPlay playsInline />
                        <canvas ref={faceCanvasRef} className="hidden" />
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                          <div
                            className="border-2 border-white/60 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"
                            style={{ width: "55%", height: "85%", borderRadius: "50% / 60%" }}
                            data-testid="face-guide-oval"
                          />
                        </div>
                        {!faceStream && (
                          <div className="absolute inset-0 flex items-center justify-center text-white/80 text-xs">Memulai kamera…</div>
                        )}
                        {POSES[poseIdx].key === "front" && livenessOk && (
                          <div className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] px-2 py-0.5 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Liveness OK</div>
                        )}
                        <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5">Pose: <b>{POSES[poseIdx].label}</b></div>
                      </div>
                      {livenessTick && POSES[poseIdx].key === "front" && (
                        <div className="text-[11px] font-mono text-stone-600 text-center" data-testid="profil-liveness-tick">
                          EAR: {Number(livenessTick.ear || 0).toFixed(3)} · Blink: {livenessTick.blinkCount || 0}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 justify-center">
                        {POSES[poseIdx].key === "front" && !livenessOk && (
                          <button onClick={runLiveness} disabled={!faceStream} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50" data-testid="profil-liveness-btn">
                            <Eye className="h-3.5 w-3.5" /> Cek Liveness (Kedipkan Mata)
                          </button>
                        )}
                        {(POSES[poseIdx].key !== "front" || livenessOk) && (
                          <button onClick={captureFace} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-emerald-600 text-white hover:bg-emerald-700" data-testid="profil-face-capture">
                            <ScanFace className="h-3.5 w-3.5" /> Ambil Pose {poseIdx + 1}
                          </button>
                        )}
                        <button onClick={stopFaceCam} className="inline-flex items-center px-3 py-2 text-xs border border-stone-300 hover:bg-stone-50">Batal</button>
                      </div>
                    </>
                  )}
                  {faStatus && <div className="text-[11px] text-stone-500 text-center">{faStatus}</div>}
                </div>
              )}
            </section>
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-4 border-t border-stone-100">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="px-4 py-2 text-sm border border-stone-300 hover:bg-stone-50 disabled:opacity-40"
              data-testid="profil-prev"
            >← Sebelumnya</button>
            {step < totalSteps ? (
              <button
                onClick={() => setStep((s) => Math.min(totalSteps, s + 1))}
                className="px-4 py-2 text-sm bg-terra-500 text-white hover:bg-terra-600"
                data-testid="profil-next"
              >Lanjut →</button>
            ) : (
              <button
                onClick={submit}
                disabled={busy}
                className="px-4 py-2 text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                data-testid="profil-submit"
              >{busy ? "Mengirim..." : "Kirim & Tunggu Persetujuan HR"}</button>
            )}
          </div>
        </div>

        <div className="text-[11px] text-stone-500 mt-3 text-center">
          Setelah dikirim, status akun Anda menjadi <b>pending_verification</b>. HR akan menyetujui & mengisi jabatan Anda sebelum bisa clock-in.
        </div>
      </div>
    </div>
  );
}
